import api from '@/services/api'
import { camelToSnake, snakeToCamel } from '../../../utils/object'

import Evaluation from '@/services/evaluations'
import ApplicationReport from '@/services/applicationReports/'

export default class ApplicationAPI {
  static APPLICATION_ENDPOINT = 'applications'
  static DRY_RUN_UPLOAD_APPLICATIONS_ENDPOINT = 'applications/dry-run-upload-applications/'
  static UPLOAD_APPLICATIONS_ENDPOINT = 'applications/upload-applications/'
  static UPLOAD_APPLICATION_FILES_ENDPOINT = 'applications/upload-application-files/'

  /**
   * Initialize a `ApplicationAPI` instance.
   *
   * @param {class} cls  The class to use to create application objects.
   **/
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `ApplicationAPI`.
   *
   * @param {class} cls      The class to use to create application objects.
   **/
  static create(cls) {
    return new ApplicationAPI(cls)
  }

  /**
   * Get the API Client.
   */
  get client() {
    return api.getApiClient()
  }

  /**
   * Retrieve one application from the API.
   *
   * @param {String} applicationID  Unique ID of the application to retrieve.
   */
  get(applicationID) {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/${applicationID}/`
    return this.client.get(url).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Get all application through the API.
   *
   *
   */
  list({ filters = {} } = {}) {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/`
    const options = {
      params: {},
    }

    if (filters.search) {
      options.params.search = filters.search
    }

    if (filters.pageSize) {
      options.params.page_size = filters.pageSize
    }

    if (filters.page) {
      options.params.page = filters.page
    }

    var dataToReturn =  this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => {
        data.results = data.results.map(this.cls.fromAPI)
        return data
      })
      console.log("list-dataToReturn")
      console.log(dataToReturn.results)

    return dataToReturn;
  }

  /**
   * Update a application through the API.
   *
   * @param {application} application data to send to the API.
   */
  update(application, fields = [], excludeFields = []) {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/${application.id}/`
    const data = this.cls.toAPI(application, fields, excludeFields)
    return this.client.patch(url, data).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * If an evaluation exists for the application, return it. Otherwise, create a new one. Returns an evaluation object.
   *
   * @param {String} applicationID  Unique ID of the application's evaluation to retrieve.
   */
  createOrReturnEvaluation(applicationID) {
    const url = `${
      ApplicationAPI.APPLICATION_ENDPOINT
    }/${applicationID}/create-or-return-evaluation/`
    return this.client.get(url).then(response => Evaluation.fromAPI(response.data))
  }

  /**
   * Return all evaluations for application. To be used by staff only.
   *
   * @param {String} applicationID  Unique ID of the application's evaluation to retrieve.
   */
  getEvaluations(applicationID) {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/${applicationID}/evaluations/`
    return this.client.get(url).then(response => response.data.map(Evaluation.fromAPI))
  }

  getReports() {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/reports/`
    return this.client.get(url).then(response => response.data.map(Evaluation.fromAPI))
  }

  getAllApplications() {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/get-all-applications/`
    var thisData =  this.client
      .get(url)
      .then(response => response.data);

    thisData = thisData.then(data => {
        // To Do, add data.results instead of data when you add pagination
        data = data.map(data => this.cls.fromAPI(data))
        return data
      })
      
    return thisData;      

  }

  /**
   * Fetch the report of a specific Application.
   *
   * @param {String} applicationID  Unique ID of the application to pull the report for.
   */
  getApplicationReport(applicationID) {
    const url = `${ApplicationAPI.APPLICATION_ENDPOINT}/${applicationID}/report/`
    return this.client.get(url).then(response => ApplicationReport.fromAPI(response.data))
  }

  /**
   * Do a dry run of uploading applications/proposals through a CSV.
   *
   * @param {File} file - CSV of applications/proposals to upload.
   */
  uploadApplicationsDryRun(file) {
    const url = ApplicationAPI.DRY_RUN_UPLOAD_APPLICATIONS_ENDPOINT
    let data = new FormData()
    data.append('file', file)
   var dataToReturn = this.client
      .post(url, data)
      .then(response => snakeToCamel(response.data))
      .catch(error => {
        console.log('Upload Applications Dry Run API Error')
        throw error
      })
    return dataToReturn    
  }

  /**
   * Upload applications/proposals to the database through a CSV.
   *
   * @param {File} file - CSV of applications/proposals to upload.
   */
  uploadApplications(file) {
    const url = ApplicationAPI.UPLOAD_APPLICATIONS_ENDPOINT
    let data = new FormData()
    data.append('file', file)

    return this.client
      .post(url, data)
      .then(response => snakeToCamel(response.data))
      .catch(error => {
        console.log('Upload Applications API Error')
        throw error
      })
  }

  /**
   * Upload applications/proposals to the database through a CSV.
   *
   * @param {File} zipfile - Zipped directory of files to upload
   */
  uploadApplicationFiles(zipfile) {
    const url = ApplicationAPI.UPLOAD_APPLICATION_FILES_ENDPOINT
    let data = new FormData()
    data.append('files', zipfile)

    return this.client
      .post(url, data)
      .then(response => snakeToCamel(response.data))
      .catch(error => {
        console.log('Upload Application Files API Error')
        throw error
      })
  }
}
